import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Instagram, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const copyDetail = (value, label) => (e) => {
  navigator.clipboard?.writeText(value).catch(() => {});
  toast.success(`${label} copied — ${value}`);
};

const reveal = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export default function Consult() {
  const [form, setForm] = useState({ name: "", email: "", goal: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. Harry will be in touch within 24 hours.");
      setForm({ name: "", email: "", goal: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "rounded-none bg-secondary/60 border-white/10 h-12 text-white placeholder:text-zinc-500 focus-visible:ring-gold focus-visible:ring-1 focus-visible:ring-offset-0";

  return (
    <section id="contact" className="py-24 md:py-40 border-b border-white/10" data-testid="contact-section">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <motion.h2
          {...reveal}
          transition={{ duration: 0.7 }}
          className="font-display font-bold uppercase leading-[0.9] text-5xl md:text-7xl lg:text-8xl mb-14 md:mb-20"
          data-testid="contact-heading"
        >
          Book Your <span className="text-gold italic">Consultation</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
          <motion.form {...reveal} transition={{ duration: 0.7 }} onSubmit={submit} className="space-y-6" data-testid="enquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="enquiry-name" className="block font-display uppercase tracking-[0.2em] text-xs text-zinc-400 mb-2">Name</label>
                <Input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={update("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="enquiry-email" className="block font-display uppercase tracking-[0.2em] text-xs text-zinc-400 mb-2">Email</label>
                <Input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="enquiry-goal" className="block font-display uppercase tracking-[0.2em] text-xs text-zinc-400 mb-2">Primary Goal</label>
              <Input id="enquiry-goal" data-testid="enquiry-goal-input" required value={form.goal} onChange={update("goal")} placeholder="e.g. Get stronger after a knee injury" className={inputCls} />
            </div>
            <div>
              <label htmlFor="enquiry-message" className="block font-display uppercase tracking-[0.2em] text-xs text-zinc-400 mb-2">Tell Us More</label>
              <Textarea id="enquiry-message" data-testid="enquiry-message-input" value={form.message} onChange={update("message")} placeholder="Injuries, experience, schedule, goals..." className={`${inputCls} min-h-[120px] h-auto py-3`} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-btn"
              className="group inline-flex items-center gap-2 bg-gold text-black font-display font-semibold uppercase tracking-[0.15em] px-10 py-4 hover:bg-gold-bright transition-colors duration-300 disabled:opacity-50"
            >
              {sending ? "Sending..." : "Request Free Consultation"}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.form>

          <motion.div {...reveal} transition={{ duration: 0.7, delay: 0.15 }} className="flex flex-col gap-8" data-testid="location-panel">
            <div className="border border-white/10" data-testid="location-map">
              <iframe
                title="OSHPerformance location map"
                src="https://www.google.com/maps?q=32%20High%20Street%2C%20Northwood%20HA6%201BN%2C%20UK&output=embed"
                className="w-full h-64 md:h-80 grayscale-[0.6] contrast-[1.05]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="location-map-iframe"
              />
              <div className="flex items-center justify-between gap-4 px-5 py-4 bg-secondary/40 border-t border-white/10">
                <div className="flex items-center gap-3 min-w-0">
                  <MapPin className="w-5 h-5 text-gold shrink-0" />
                  <p className="text-zinc-300 text-sm truncate" data-testid="location-address">32 High Street, Northwood HA6 1BN</p>
                </div>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=32+High+Street+Northwood+HA6+1BN"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="location-directions-link"
                  className="font-display uppercase tracking-[0.15em] text-xs text-gold hover:text-gold-bright transition-colors whitespace-nowrap"
                >
                  Get Directions
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="border border-white/10 p-6 flex items-start gap-4">
                <Phone className="w-5 h-5 text-gold mt-1 shrink-0" />
                <div>
                  <p className="font-display uppercase tracking-[0.2em] text-xs text-zinc-400 mb-2">Contact Me</p>
                  <p className="text-white text-sm leading-relaxed" data-testid="location-phone">
                    <a href="tel:+447502326927" onClick={copyDetail("+44 7502 326927", "Phone number")} data-testid="contact-phone-link" className="hover:text-gold transition-colors duration-300">+44 7502 326927</a>
                    <br />
                    <a href="mailto:enquiries@oshperformance.com" onClick={copyDetail("enquiries@oshperformance.com", "Email")} data-testid="contact-email-link" className="hover:text-gold transition-colors duration-300 underline decoration-gold/40 underline-offset-4">enquiries@oshperformance.com</a>
                  </p>
                </div>
              </div>
              <a
                href="https://instagram.com/_oshperformance"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="border border-white/10 p-6 flex items-start gap-4 hover:border-gold/60 transition-colors duration-300 group"
              >
                <Instagram className="w-5 h-5 text-gold mt-1 shrink-0" />
                <div>
                  <p className="font-display uppercase tracking-[0.2em] text-xs text-zinc-400 mb-2">Follow The Journey</p>
                  <p className="text-white text-sm leading-relaxed group-hover:text-gold transition-colors duration-300">@_oshperformance<br /><span className="text-zinc-500">Training clips & client wins</span></p>
                </div>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
